import React from 'react';
import './FloatingUndoButton.css';

interface FloatingUndoButtonProps {
  position: { x: number; y: number };
  onUndo: () => void;
  onDismiss: () => void;
}

const FloatingUndoButton: React.FC<FloatingUndoButtonProps> = ({ position, onUndo, onDismiss }) => {
  const [isDisappearing, setIsDisappearing] = React.useState(false);
  const buttonRef = React.useRef<HTMLDivElement>(null);

  const handleDismiss = () => {
    setIsDisappearing(true);
    setTimeout(() => {
      onDismiss();
    }, 200); // Match the animation duration
  };

  const handleUndo = () => {
    onUndo(); // Execute undo immediately
    setIsDisappearing(true);
    setTimeout(() => {
      onDismiss(); // Clean up after animation
    }, 200); // Match the animation duration
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        handleDismiss();
      }
    };
    const clickTimeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);
    const autoDismissTimer = setTimeout(() => {
      handleDismiss();
    }, 3000);
    return () => {
      clearTimeout(clickTimeoutId);
      clearTimeout(autoDismissTimer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={buttonRef}
      className={`grocery-list__floating-undo${isDisappearing ? ' grocery-list__floating-undo--disappearing' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <button
        type="button"
        onClick={handleUndo}
        className="grocery-list__floating-undo-button"
        title="Undo mark as done"
      >
        ↺ Undo
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        className="grocery-list__floating-undo-dismiss"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

export default FloatingUndoButton; 